import { ChoiceFilterValueSelector } from "@app/filter/ChoiceFilterValueSelector";
import { DateFilterValueEditor } from "@app/filter/DateFilterValueEditor";
import { PeriodFilterValueEditor } from "@app/filter/PeriodFilterValueEditor";
import { PrimitiveFilterValueSelector } from "@app/filter/PrimitiveFilterValueSelector";
import { RecordFilterValueSelector } from "@app/filter/RecordFilterValueSelector";
import { UserFilterValueSelector } from "@app/filter/UserFilterValueSelector";
import { hasPeriodValueFilter } from "@app/filter/periodFilterLogic";
import { Command, CommandItem } from "@comps/ui/command";
import { Label } from "@comps/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { TreeDataItem, TreeView } from "@comps/ui/tree-view";
import { Widget } from "@dto/Dashboard";
import { AggregationType, FieldType, TYPE_ITEMS_FIELD_KEY, fieldToOutputType } from "@dto/fields";
import { AND, Filter } from "@dto/records";
import { getFieldKey, getValidOperator, pathKey, pathKeyToPath } from "@utils/model";
import { toastError } from "@utils/toasts";
import { buildPathTree, getTreePathValue } from "@utils/tree";
import { TW_DashboardCardFieldSelected, TW_DashboardCardFieldText, TW_DashboardCardTreeView, TW_DashboardPathSelector, TW_DashboardPathSelectorTooltip, TW_FilterEditorRemoveFilter, TW_FilterEditorRemoveFilterDisabled, TW_WidgetFilterEditor, TW_WidgetFilterEditorFilterItem, TW_WidgetFilterEditorNewFilter, TW_WidgetFilterEditorNewFilterLabel, useIconHover } from "@utils/twClasses";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";


interface WidgetFilterEditorProps {
    widget: Widget | undefined,
    srcObjectKey: string,
    onFilterChange: (filter: Filter | null) => void
    fieldAggregationByPathKeys: Map<string, AggregationType | undefined>
}

function WidgetFilterEditor({ widget, srcObjectKey, onFilterChange, fieldAggregationByPathKeys }: WidgetFilterEditorProps) {

    const [widgetFilter, setWidgetFilter] = useState<Filter | null>(widget?.data?.filter || null);
    const { t } = useTranslation();

    const onWidgetFilterChange = (newFilterToValidate: Filter) => {
        const changedFilterPathKey = newFilterToValidate.groupFilter?.filters?.[0]?.singleFilter?.pathKey;
        const newFilters = (widgetFilter?.groupFilter?.filters || []).map(f => {
            if (f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey === changedFilterPathKey) {
                return newFilterToValidate;
            }
            return f;
        });
        const newFilter = newFilters.find(f => f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey === changedFilterPathKey);
        if (!newFilter) {
            newFilters.push(newFilterToValidate);
        }

        const newWidgetFilter: Filter = {
            groupFilter: {
                filters: newFilters,
                combinator: AND
            },
            singleFilter: null
        }

        setWidgetFilter(newWidgetFilter);
        onFilterChange(newWidgetFilter);
    }

    const onFilterRemoved = (pathKey: string) => {
        const newFilters = (widgetFilter?.groupFilter?.filters || []).filter(f =>
            f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey !== pathKey);
        const newWidgetFilter: Filter = {
            groupFilter: { filters: newFilters, combinator: AND },
            singleFilter: null
        };
        setWidgetFilter(newWidgetFilter);
        onFilterChange(newWidgetFilter);
    }

    let filterByPathKeys: Map<string, Filter> = new Map();
    const filterPathKeys: string[] = [];
        if (widgetFilter && widgetFilter.groupFilter &&
            widgetFilter.groupFilter.filters &&
            widgetFilter.groupFilter.filters.length > 0) {
            widgetFilter.groupFilter.filters.forEach(filter => {
                if (filter.groupFilter && filter.groupFilter.filters && filter.groupFilter.filters.length > 0) {
                    const key = filter.groupFilter.filters[0].singleFilter?.pathKey;
                    if (key) {
                    filterByPathKeys.set(key, filter);
                    filterPathKeys.push(key);
                    }
                }
            });
        }

    const sortedFilterByPathKeys = Array.from(filterByPathKeys.keys()).sort();
    return (
        <div className={TW_WidgetFilterEditor}>
            {filterPathKeys.length > 0 &&
                filterPathKeys.map((key, index) => {
                    const f = filterByPathKeys.get(key)!;
                    return <WidgetFilterItem key={"filter-" + key + "-" + index} filter={f} onFilterChange={onWidgetFilterChange} onFilterRemoved={onFilterRemoved} srcObjectKey={srcObjectKey} usedFilterPathKeys={sortedFilterByPathKeys} fieldAggregationByPathKeys={fieldAggregationByPathKeys} />
                })}
            <div className={TW_WidgetFilterEditorNewFilter}>
                <Label className={TW_WidgetFilterEditorNewFilterLabel}>{t("dashboard.widget.filter.add")}</Label>
                <WidgetFilterItem key={"filter-new" + filterPathKeys.length} filter={null} onFilterChange={onWidgetFilterChange} onFilterRemoved={onFilterRemoved} srcObjectKey={srcObjectKey} usedFilterPathKeys={sortedFilterByPathKeys} fieldAggregationByPathKeys={fieldAggregationByPathKeys} />
            </div>
        </div>
    )
}

interface WidgetFilterItemProps {
    filter: Filter | null,
    onFilterChange: (filter: Filter) => void,
    onFilterRemoved: (pathKey: string) => void,
    srcObjectKey: string,
    usedFilterPathKeys: string[],
    fieldAggregationByPathKeys: Map<string, AggregationType | undefined>
}

function WidgetFilterItem({ filter, onFilterChange, onFilterRemoved, srcObjectKey, usedFilterPathKeys, fieldAggregationByPathKeys }: WidgetFilterItemProps) {

    const [selectedPathKey, setSelectedPathKey] = useState<string | undefined>(undefined);
    const [selectedPathName, setSelectedPathName] = useState<string | undefined>(undefined);
    const relationByKeys = useModelStore((state) => state.relationByKeys);
    const fieldByKeys = useModelStore((state) => state.fieldByKeys);
    const wsColor = useModelStore((state) => state.wsColor);
    const trashHover = useIconHover("#00000088", wsColor);
    const objectInfos = useModelStore((state) => state.objectInfos);
    const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
    const objectByKeys = useModelStore((state) => state.objectByKeys);
    const { t } = useTranslation();

    useEffect(() => {
        if (filter) {
            const fieldFilter = filter.groupFilter;
            const filters = fieldFilter?.filters;
            if (filters) {
                const firstFilter = filters[0];
                setSelectedPathKey(firstFilter.singleFilter?.pathKey);
                const path = pathKeyToPath(firstFilter.singleFilter?.pathKey, relationByKeys);
                if (path) {
                    const object = objectByKeys?.get(path.object);
                    const field = fieldByKeys?.get(path.field);
                    setSelectedPathName(object?.object.name + " > " + field?.name);
                }
            }
        }
    }, [filter]);

    const validateFilter = (newFilterToValidate: Filter) => {
        onFilterChange(newFilterToValidate);
    }

    const onRemoveFilter = () => {
        if (selectedPathKey) {
            setSelectedPathKey(undefined);
            setSelectedPathName(undefined);
            onFilterRemoved(selectedPathKey);
        }
    }

    function onPathChange(id: string | undefined) {
        if (!objectByKeys || !relationByKeys) {
            setSelectedPathKey(undefined);
            return;
        }
        const treePathValue = getTreePathValue(id, objectByKeys, relationByKeys, true);
        if (treePathValue) {
            const pKey = pathKey(treePathValue.path);
            if (usedFilterPathKeys.includes(pKey)) {
                toastError(t("dashboard.widget.filter.error.alreadyUsed"));
            } else {
                setSelectedPathKey(pKey);
                setSelectedPathName(treePathValue.name);
            }
        } else {
            setSelectedPathKey(undefined);
            setSelectedPathName(undefined);
        }

    }

    var pathTree: TreeDataItem | null = null;
    if (srcObjectKey) {
        pathTree = buildPathTree(objectInfos, sharedObjectInfos, srcObjectKey, onPathChange);
    }

    const selectedObject = objectByKeys?.get(srcObjectKey);
    const fields = selectedObject?.fields;
    if (!fields) {
        return null;
    }

    const selectedPath = selectedPathKey ? pathKeyToPath(selectedPathKey, relationByKeys) : null;
    const fieldKey = selectedPathKey ? getFieldKey(selectedPathKey) : null;
    const field = fieldKey ? (fieldByKeys?.get(fieldKey.fieldKey) || null) : null;
    const isLink = selectedPath?.edges?.length !== undefined && selectedPath.edges.length > 0;
    const disabledTrash = !selectedPathKey;

    let outputType = field?.type;
    let selectedFieldAggregation: AggregationType | undefined = undefined;
    if (field && selectedPathKey) {
        selectedFieldAggregation = fieldAggregationByPathKeys?.get(selectedPathKey);
        outputType = fieldToOutputType(field, selectedFieldAggregation, fieldByKeys);
        if (isLink) {
            const typeItems = field.typeItems as Map<string, any>;
            const fieldKey = typeItems?.get(TYPE_ITEMS_FIELD_KEY);
            if (fieldKey) {
                const linkedField = fieldByKeys?.get(fieldKey);
                if (linkedField) {
                    outputType = fieldToOutputType(linkedField, selectedFieldAggregation, fieldByKeys);
                }
            }
        }
    }
    const validOperators = outputType ? getValidOperator(outputType, field?.typeItems) : [];
    // A period value set while the column was aggregated by period stays
    // visible and editable once the aggregation is removed: the date editor
    // cannot display an IN filter value.
    const usePeriodEditor = outputType === FieldType.Period || (outputType === FieldType.Date && hasPeriodValueFilter(filter));
    return (
        <div className={TW_WidgetFilterEditorFilterItem}>
            {!selectedPathKey && pathTree && <TreeView searchable data={pathTree} className={TW_DashboardCardTreeView} />}
            {selectedPathKey &&
                <Command className={TW_DashboardPathSelector}>
                    <TooltipProvider>
                        <CommandItem className={TW_DashboardCardFieldSelected}>
                            <Tooltip delayDuration={100}>
                                <TooltipTrigger className={TW_DashboardPathSelectorTooltip}>
                                    <span className={TW_DashboardCardFieldText}>{selectedPathName}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {selectedPathName}
                                </TooltipContent>
                            </Tooltip>
                        </CommandItem>
                    </TooltipProvider>
                </Command>

            }
            {selectedPathKey && isLink && (outputType === FieldType.Text || outputType === FieldType.TextArea || outputType === FieldType.Url) &&
                <RecordFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={false} />
            }
            {selectedPathKey && outputType == FieldType.User &&
                <UserFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={false} />
            }
            {selectedPathKey && !isLink && (outputType == FieldType.Text || outputType == FieldType.TextArea || outputType == FieldType.Url) &&
                <PrimitiveFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={false} />
            }
            {selectedPathKey && (outputType == FieldType.Number || outputType == FieldType.Boolean || outputType == FieldType.Decimal || outputType == FieldType.Percent || outputType == FieldType.Devise) &&
                <PrimitiveFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={false} />
            }
            {selectedPathKey && (outputType == FieldType.Choice || outputType == FieldType.MultiChoice) &&
                <ChoiceFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={false} />
            }
            {selectedPathKey && (outputType == FieldType.Date) && !usePeriodEditor &&
                <DateFilterValueEditor selectedPathKey={selectedPathKey} filter={filter!} validateFilter={validateFilter} disabled={false} />
            }
            {selectedPathKey && usePeriodEditor &&
                <PeriodFilterValueEditor field={field} aggregation={selectedFieldAggregation} selectedPathKey={selectedPathKey} filter={filter!} validateFilter={validateFilter} disabled={false} />
            }
            {selectedPathKey && !fieldKey &&
                <PrimitiveFilterValueSelector selectedPathKey={selectedPathKey} filter={null} validOperators={[]} validateFilter={validateFilter} disabled={false} />
            }
            {selectedPathKey && disabledTrash && <Trash2 className={TW_FilterEditorRemoveFilterDisabled} />}
            {selectedPathKey && !disabledTrash && <Trash2 className={TW_FilterEditorRemoveFilter} onClick={onRemoveFilter} {...trashHover} />}
        </div>
    )
}

export default WidgetFilterEditor;
